import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import {
  applyTheme,
  DARK_MEDIA_QUERY,
  readStoredThemeMode,
  resolveTheme,
  systemPrefersDark,
  writeStoredThemeMode,
  type ThemeMode,
} from "@/lib/theme";
import { ThemeContext } from "@/lib/theme-context";

/** Тема портала. Начальное состояние уже выставил скрипт в `index.html` (до первой отрисовки); провайдер
 *  держит его дальше: выбор пользователя и живое переключение вслед за системой в режиме `system`. */
export function ThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [mode, setModeState] = useState<ThemeMode>(readStoredThemeMode);
  const [prefersDark, setPrefersDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    const mql = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = (e: { matches: boolean }): void =>
      setPrefersDark(e.matches);
    mql.addEventListener("change", onChange);
    setPrefersDark(mql.matches); // система могла смениться между первым рендером и подпиской
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolved = resolveTheme(mode, prefersDark);
  useLayoutEffect(() => applyTheme(resolved), [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    writeStoredThemeMode(next);
    setModeState(next);
  }, []);

  const value = useMemo(
    () => ({ mode, resolved, setMode }),
    [mode, resolved, setMode],
  );
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
